import React, { useRef, useState } from "react";
import { motion } from "framer-motion";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  ShoppingCart,
  CaretLeft,
  Check,
  X,
  CreditCard,
  Wallet,
} from "@phosphor-icons/react";
import { HashLoader } from "react-spinners";
import { Field, Form, Formik } from "formik";
import * as yup from "yup";
import { toast } from "sonner";
import {
  CheckoutHeldDetails,
  getCheckoutHeldDetails,
  useCompleteCartPayment,
} from "../../../hooks/useCompleteCartPayment";
import { useAuth } from "../../../context/AuthContext";
import useCard from "../../../hooks/useCard";
import { formatAsMoney } from "../../../helpers/numbers";
import PaymentHeldOverlay from "./PaymentHeldOverlay";

const SCHEMA = yup.object().shape({
  accepted: yup.bool().isTrue("You have to accept the terms and conditions"),
});

const toNumber = (value: unknown) => {
  if (typeof value === "number") {
    return value;
  }

  if (typeof value === "string") {
    return Number(value.replace(/[^\d.-]/g, "")) || 0;
  }

  return 0;
};

const getPaymentOptionCode = (paymentData: any) =>
  paymentData?.option?.code || paymentData?.option?.id || "";

const getPaymentDescription = (paymentData: any) => {
  // Branch on the option code only — it is the authoritative rail the backend
  // executes (001 = card, 003 = wallet). Mixing tag OR code previously let one
  // option be classified as both wallet and card.
  const code = getPaymentOptionCode(paymentData);

  if (code === "003") {
    return "Pay instantly using your wallet balance.";
  }

  if (code === "001") {
    return "Pay instantly with your saved card.";
  }

  return paymentData?.option?.description;
};

const PayNow = () => {
  const { state: params } = useLocation();

  const { user, refetch } = useAuth();
  const { card, isLoading: isLoadingCard } = useCard();

  const { orderId } = useParams();

  const navigate = useNavigate();

  const [confirming, setConfirming] = useState(false);
  const [bookingError, setOrderError] = useState(false);
  const [booked, setBooked] = useState(false);
  const [heldDetails, setHeldDetails] = useState<CheckoutHeldDetails | null>(
    null
  );
  // Survives re-renders without one, so a double click can't fire two charges.
  const submittingRef = useRef(false);
  const paymentOptionCode = getPaymentOptionCode(params?.paymentData);
  // Authoritative rail = option code (001 = card, 003 = wallet), matching the
  // backend executor. A single source avoids the dual wallet/card classification.
  const isCardPayment = paymentOptionCode === "001";
  const isWalletPayment = paymentOptionCode === "003";
  const linkedCard = card?.data;
  const totalAmountPayable = toNumber(
    params?.paymentData?.payment_data?.total_amount_payable ??
      params?.paymentData?.payment_data?.formatted?.total_amount_payable
  );
  const availableBalance = toNumber(user?.wallet?.available_balance);
  const hasInsufficientBalance =
    isWalletPayment && availableBalance < totalAmountPayable;

  const {
    completeCartPayment,
    completeCartPaymentError,
    completingCartPayment,
    isCompleteCartPaymentError,
    completeCartPaymentData,
  } = useCompleteCartPayment({
    onError(error) {
      refetch();
      const held = getCheckoutHeldDetails(error);
      if (held) {
        setHeldDetails(held);
        return;
      }
      setOrderError(true);
      toast.error(error.message);
    },
    onSuccess(data) {
      refetch();
      setBooked(true);
      toast.success(data.message);
    },
    onSettled() {
      submittingRef.current = false;
    },
  });

  return (
    <div>
      {completingCartPayment && (
        <div className="fixed top-0 z-[99] left-0 h-screen w-screen flex items-center justify-center bg-black/20 backdrop-blur-sm">
          <HashLoader />
        </div>
      )}
      {confirming && (
        <motion.div
          initial={{ y: "100%" }}
          animate={{ y: 0 }}
          exit={{ y: "100%" }}
          transition={{
            type: "tween",
            ease: "anticipate",
          }}
          className="flex items-center justify-center gap-10 py-20 text-center flex-col bg-white absolute top-0 left-0 w-full z-50 h-full"
        >
          <div className="max-w-2xl flex flex-col items-center justify-center gap-8">
            <div className="bg-visaro-primary-700 rounded-full p-8 text-white">
              <ShoppingCart size={36} />
            </div>
            <div className="text-center flex flex-col gap-2">
              <div className="text-3xl font-medium">Are you sure?</div>
              <div>
                You are about to be charged ₦{" "}
                <span className="font-medium">
                  {
                    params?.paymentData?.payment_data?.formatted
                      ?.total_amount_payable
                  }{" "}
                </span>
                for this order.
              </div>
            </div>
            <div className="flex justify-between w-full">
              <button
                onClick={() => setConfirming(false)}
                type="button"
                className="p-2 py-3 text-center bg-red-100 text-red-500 px-6 ring-2 ring-red-100/50 rounded-xl font-medium flex gap-2 items-center"
              >
                <span>Cancel</span>
              </button>
              <button
                onClick={() => {
                  if (hasInsufficientBalance) {
                    toast.error("Insufficient wallet balance");
                    return;
                  }

                  if (submittingRef.current) return;
                  submittingRef.current = true;

                  setConfirming(false);
                  completeCartPayment({
                    payment_option_id: paymentOptionCode,
                    loan_id: params?.paymentData?.payment_data?.loan_id,
                    card_id: isCardPayment ? linkedCard?.id : undefined,
                    session_id: orderId!!,
                  });
                }}
                disabled={hasInsufficientBalance || completingCartPayment}
                className={`p-2 py-3 text-center bg-deepBlue text-white px-10 ring-2 ring-deepBlue/50 rounded-xl font-medium ${
                  hasInsufficientBalance || completingCartPayment
                    ? "opacity-50 cursor-not-allowed"
                    : ""
                }`}
              >
                Pay
              </button>
            </div>
          </div>
        </motion.div>
      )}
      {heldDetails && (
        <PaymentHeldOverlay
          details={heldDetails}
          message={completeCartPaymentError?.message}
          onViewOrders={() => navigate("/payments/orders")}
        />
      )}
      {isCompleteCartPaymentError && bookingError && (
        <motion.div
          initial={{ y: "100%" }}
          animate={{ y: 0 }}
          exit={{ y: "100%" }}
          transition={{
            type: "tween",
            ease: "anticipate",
          }}
          className="flex items-center justify-center gap-10 py-20 text-center flex-col bg-white absolute top-0 left-0 w-full z-50 h-full"
        >
          <div className="max-w-2xl flex flex-col items-center justify-center gap-8">
            <div className="bg-red-500 rounded-full p-8 text-white">
              <X size={36} />
            </div>
            <div className="flex flex-col items-center gap-2 w-full">
              <div className="text-3xl font-medium">Order Failed!</div>
              <div className="w-full line-clamp-6">
                {completeCartPaymentError?.message.toString()}
              </div>
            </div>
            <button
              onClick={() => setOrderError(false)}
              type="button"
              className="p-2 py-3 text-center bg-red-100 text-red-500 px-6 ring-2 ring-red-100/50 rounded-xl font-medium flex gap-2 items-center"
            >
              <span>Cancel</span>
            </button>
          </div>
        </motion.div>
      )}
      {booked && (
        <motion.div
          initial={{ y: "100%" }}
          animate={{ y: 0 }}
          exit={{ y: "100%" }}
          transition={{
            type: "tween",
            ease: "anticipate",
          }}
          className="flex items-center justify-center gap-10 py-20 text-center flex-col bg-white absolute top-0 left-0 w-full z-50 h-full"
        >
          <div className="max-w-2xl flex flex-col items-center justify-center gap-8">
            <div className="bg-green-500 rounded-full p-8 text-white">
              <Check size={36} />
            </div>
            <div className="text-center flex flex-col gap-2">
              <div className="text-3xl font-medium">Order confirmed!</div>
              <div>
                Congratulations! Your order has been sent to the merchants. An
                email will be sent to you containing the order details.
              </div>
            </div>
            <button
              onClick={() => {
                const orders = (completeCartPaymentData?.data as any)?.orders;

                if (!orders?.length) {
                  toast.error(
                    "Payment completed, but no order was returned. Please check your orders page."
                  );
                  return;
                }

                if (orders.length > 1) {
                  navigate("/payments/orders");
                  setBooked(false);
                  return;
                }

                navigate("/payments/orders/" + orders[0].public_id);
                setBooked(false);
              }}
              type="button"
              className="p-2 py-3 text-center bg-deepBlue text-white px-10 ring-2 ring-deepBlue/50 rounded-xl font-medium"
            >
              <span>View Order</span>
            </button>
          </div>
        </motion.div>
      )}
      <Formik
        validationSchema={SCHEMA}
        initialValues={{
          accepted: false,
        }}
        onSubmit={() => {
          if (hasInsufficientBalance) {
            toast.error("Insufficient wallet balance");
            return;
          }

          setConfirming(true);
        }}
      >
        {({ isValid, dirty }) => {
          return (
            <Form className="relative w-full flex items-center justify-center gap-10 px-5 py-20 flex-col">
              <div className="flex flex-col gap-5 max-w-4xl w-full items-start">
                <div className="font-semibold text-3xl">Pay Now</div>
                <div className="text-gray-500">You'll be charged</div>
                <div className="text-3xl font-semibold">
                  ₦{" "}
                  {
                    params?.paymentData?.payment_data?.formatted
                      ?.total_amount_payable
                  }
                </div>
                <div>{getPaymentDescription(params?.paymentData)}</div>
                {isCardPayment && (
                  <div className="w-full rounded-xl border border-gray-200 bg-white p-4 shadow-sm flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <div className="p-3 rounded-full bg-deepBlue/10 text-deepBlue">
                        <CreditCard size={24} />
                      </div>
                      <div className="flex flex-col gap-1">
                        <div className="font-medium">Card payment</div>
                        {isLoadingCard ? (
                          <div className="text-sm text-gray-500">
                            Loading linked card...
                          </div>
                        ) : linkedCard ? (
                          <div className="text-sm text-gray-500">
                            Paying with {linkedCard.card_type} ending in{" "}
                            {linkedCard.last_four_digit}
                          </div>
                        ) : (
                          <div className="text-sm text-gray-500">
                            No card linked
                          </div>
                        )}
                      </div>
                    </div>
                    <Link
                      to="/wallet"
                      className="text-sm font-medium text-visaro-orange underline whitespace-nowrap"
                    >
                      {linkedCard ? "Change card" : "Add a card"}
                    </Link>
                  </div>
                )}
                {isWalletPayment && (
                  <div className="w-full rounded-xl border border-gray-200 bg-white p-4 shadow-sm flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <div className="p-3 rounded-full bg-deepBlue/10 text-deepBlue">
                        <Wallet size={24} />
                      </div>
                      <div className="flex flex-col gap-1">
                        <div className="font-medium">Wallet payment</div>
                        <div className="text-sm text-gray-500">
                          Available balance: ₦
                          {formatAsMoney(availableBalance)}
                        </div>
                        {hasInsufficientBalance && (
                          <div className="text-sm text-red-500 font-medium">
                            Insufficient balance
                          </div>
                        )}
                      </div>
                    </div>
                    {hasInsufficientBalance && (
                      <Link
                        to="/home"
                        className="text-sm font-medium text-visaro-orange underline whitespace-nowrap"
                      >
                        Fund your wallet
                      </Link>
                    )}
                  </div>
                )}
                <br />
                <div
                  style={{
                    flexDirection: "row",
                  }}
                >
                  <div
                    style={{
                      gap: 2,
                      flex: 1,
                    }}
                  >
                    <div className="font-medium">Transaction Cost</div>
                    <div className="text-lg">
                      ₦
                      {
                        params?.paymentData?.payment_data?.formatted
                          ?.processing_fee
                      }{" "}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Field
                    type="checkbox"
                    name="accepted"
                    className="w-4 aspect-square accent-deepBlue"
                  />{" "}
                  <span>
                    By clicking ‘Continue’ , you agree to{" "}
                    <Link
                      to=""
                      className="text-visaro-orange font-medium underline"
                    >
                      Terms and Condition
                    </Link>{" "}
                    &{" "}
                    <Link
                      to=""
                      className="text-visaro-orange font-medium underline"
                    >
                      Privacy Policy
                    </Link>
                  </span>
                </div>
              </div>
              <div className="flex justify-between w-full max-w-4xl">
                <button
                  onClick={() => navigate(-1)}
                  type="button"
                  className="p-2 py-3 text-center text-deepBlue border px-6 rounded-xl font-medium flex gap-2 items-center border-gray-300"
                >
                  <CaretLeft />
                  <span>Back</span>
                </button>
                <button
                  disabled={!isValid || !dirty || hasInsufficientBalance}
                  type="submit"
                  className={`p-2 py-3 text-center bg-deepBlue text-white px-10 ring-2 ring-deepBlue/50 rounded-xl font-medium ${
                    !isValid || !dirty || hasInsufficientBalance
                      ? "opacity-50 cursor-not-allowed"
                      : ""
                  }`}
                >
                  Pay
                </button>
              </div>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

export default PayNow;
